import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { FormAlert, toHebrewNumeral } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';

const FRAMES = [
  { value: 'daf_yomi', label: 'דף היומי' },
  { value: 'shinun', label: 'דף השינון' },
  { value: 'meshannim', label: 'משננים' },
];

const ALLOWED_FRAMES = new Set(FRAMES.map((f) => f.value));
const FRAME_LABELS = Object.fromEntries(FRAMES.map((f) => [f.value, f.label]));

const SECTION_LABELS = {
  main: 'ראשי',
  zchut: 'זכות',
  tosefet: 'תוספות',
  lechiduda: 'לחידודא',
  tzurta: 'צורתא דשמעתתא',
};

export const SECTION_OPTIONS = [
  { value: 'main', label: 'ראשי' },
  { value: 'zchut', label: 'זכות' },
  { value: 'tosefet', label: 'תוספות' },
  { value: 'lechiduda', label: 'לחידודא' },
  { value: 'tzurta', label: 'צורתא דשמעתתא' },
];

const SECTION_ORDER = SECTION_OPTIONS.map((o) => o.value);

function formatMaterial(material, frame) {
  if (!material?.tractate) return '';
  const { tractate, dafFrom, dafTo, amudFrom, amudTo } = material;
  const tractateTo = material.tractateTo || tractate;
  const fromHeb = toHebrewNumeral(dafFrom);
  const toHeb = toHebrewNumeral(dafTo);
  const amudHeb = { a: 'א', b: 'ב' };
  const cross = tractateTo !== tractate;

  if (frame === 'shinun' && amudFrom && amudTo) {
    const fromPart = `${tractate} דף ${fromHeb} עמוד ${amudHeb[amudFrom] || amudFrom}`;
    const toPart = cross
      ? `${tractateTo} דף ${toHeb} עמוד ${amudHeb[amudTo] || amudTo}`
      : `דף ${toHeb} עמוד ${amudHeb[amudTo] || amudTo}`;
    return `${fromPart} – ${toPart}`;
  }
  if (!cross && dafFrom === dafTo) return `${tractate} דף ${fromHeb}`;
  if (!cross) return `${tractate} דף ${fromHeb}–${toHeb}`;
  return `${tractate} דף ${fromHeb} – ${tractateTo} דף ${toHeb}`;
}

export function emptyQuestion(sectionKey = 'main') {
  return {
    sectionKey,
    text: '',
    options: ['', '', ''],
    correctOption: 1,
  };
}

export function normalizeLoadedQuestions(questions = []) {
  return [...questions]
    .sort((a, b) => {
      const sa = SECTION_ORDER.indexOf(a.sectionKey || 'main');
      const sb = SECTION_ORDER.indexOf(b.sectionKey || 'main');
      if (sa !== sb) return sa - sb;
      return (a.number || 0) - (b.number || 0);
    })
    .map((q) => ({
      sectionKey: q.sectionKey || 'main',
      text: q.text || '',
      options: [
        String(q.options?.[0] ?? ''),
        String(q.options?.[1] ?? ''),
        String(q.options?.[2] ?? ''),
      ],
      correctOption: [1, 2, 3].includes(Number(q.correctOption))
        ? Number(q.correctOption)
        : 1,
    }));
}

export function sectionSummary(questions = []) {
  const counts = {};
  for (const q of questions) {
    counts[q.sectionKey] = (counts[q.sectionKey] || 0) + 1;
  }
  return SECTION_OPTIONS
    .filter((s) => counts[s.value] > 0)
    .map((s) => `${s.label} ${counts[s.value]}`)
    .join(' · ');
}

const AmericanExamEditor = () => {
  const { frame: frameParam, examId } = useParams();
  const frame = String(frameParam || '').trim();
  const navigate = useNavigate();

  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [flash, setFlash] = useState(null);
  const [error, setError] = useState('');

  const listPath = `/american-exams/${frame || 'daf_yomi'}`;

  const load = useCallback(async () => {
    if (!examId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/american-exams/${examId}`);
      setExam(data);
      const loaded = normalizeLoadedQuestions(data.questions || []);
      setQuestions(loaded.length ? loaded : [emptyQuestion('main')]);
      setDirty(loaded.length === 0);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בטעינת המבחן');
      setExam(null);
    } finally {
      setLoading(false);
    }
  }, [examId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 5000);
    return () => clearTimeout(t);
  }, [flash]);

  const updateQuestion = (index, patch) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
    setDirty(true);
    setError('');
  };

  const updateOption = (index, optIndex, value) => {
    setQuestions((prev) => prev.map((q, i) => {
      if (i !== index) return q;
      const options = [...q.options];
      options[optIndex] = value;
      return { ...q, options };
    }));
    setDirty(true);
    setError('');
  };

  const moveQuestion = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= questions.length) return;
    setQuestions((prev) => {
      const next = [...prev];
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
    setDirty(true);
  };

  const removeQuestion = (index) => {
    if (questions.length <= 1) {
      setError('חייבת להישאר לפחות שאלה אחת');
      return;
    }
    setQuestions((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
    setError('');
  };

  const addQuestion = (afterIndex = null) => {
    setQuestions((prev) => {
      const q = emptyQuestion(prev[afterIndex]?.sectionKey || 'main');
      if (afterIndex == null || afterIndex < 0 || afterIndex >= prev.length - 1) {
        return [...prev, q];
      }
      const next = [...prev];
      next.splice(afterIndex + 1, 0, q);
      return next;
    });
    setDirty(true);
    setError('');
  };

  const validateLocal = () => {
    if (!questions.length) return 'נדרשת לפחות שאלה אחת';
    for (let i = 0; i < questions.length; i += 1) {
      const q = questions[i];
      if (!SECTION_LABELS[q.sectionKey]) return `שאלה ${i + 1}: סעיף לא תקין`;
      if (!q.options.every((o) => String(o || '').trim())) {
        return `שאלה ${i + 1}: יש למלא את כל 3 האפשרויות`;
      }
      if (![1, 2, 3].includes(Number(q.correctOption))) {
        return `שאלה ${i + 1}: יש לבחור תשובה נכונה`;
      }
    }
    if (exam?.status === 'active' && !questions.some((q) => q.sectionKey === 'main')) {
      return 'מבחן פעיל חייב לכלול לפחות שאלה אחת בסעיף הראשי';
    }
    return '';
  };

  const handleSave = async () => {
    const localErr = validateLocal();
    if (localErr) {
      setError(localErr);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        questions: questions.map((q) => ({
          sectionKey: q.sectionKey,
          text: String(q.text || '').trim(),
          options: q.options.map((o) => String(o || '').trim()),
          correctOption: Number(q.correctOption),
        })),
      };
      const { data } = await api.put(`/american-exams/${examId}/questions`, payload);
      const saved = data.exam || data;
      setExam(saved);
      setQuestions(normalizeLoadedQuestions(saved.questions || payload.questions));
      setDirty(false);
      setFlash({ severity: 'success', message: `נשמר · ${payload.questions.length} שאלות` });
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירת השאלות');
    } finally {
      setSaving(false);
    }
  };

  const summary = useMemo(() => sectionSummary(questions), [questions]);

  if (!ALLOWED_FRAMES.has(frame)) {
    return <Navigate to="/american-exams/daf_yomi" replace />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!exam) {
    return (
      <Box sx={{ p: 2 }}>
        <FormAlert severity="error">{error || 'מבחן לא נמצא'}</FormAlert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackIcon />} onClick={() => navigate(listPath)}>
          חזרה לרשימה
        </Button>
      </Box>
    );
  }

  if (exam.frame && ALLOWED_FRAMES.has(exam.frame) && exam.frame !== frame) {
    return <Navigate to={`/american-exams/${exam.frame}/${examId}`} replace />;
  }

  const materialLabel = formatMaterial(exam.material, exam.frame);

  return (
    <Box sx={{ pb: 4 }}>
      <AdminPageHeader
        title={exam.title || 'עריכת מבחן'}
        sub={[
          FRAME_LABELS[exam.frame] || exam.frame,
          materialLabel,
          exam.status === 'active' ? 'פעיל' : 'כבוי',
          `${questions.length} שאלות`,
          summary,
        ].filter(Boolean).join(' · ')}
        back={{ label: 'מבחנים אמריקאים', onClick: () => navigate(listPath) }}
        actions={(
          <Button variant="contained" disabled={saving || !dirty} onClick={handleSave}>
            {saving ? 'שומר...' : 'שמירת שאלות'}
          </Button>
        )}
        legacy={(
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1.5,
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
                <IconButton
                  size="small"
                  onClick={() => navigate(listPath)}
                  aria-label="חזרה"
                  sx={{ mt: 0.25 }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                    {exam.title || 'עריכת מבחן'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {[
                      FRAME_LABELS[exam.frame] || exam.frame,
                      materialLabel,
                      exam.status === 'active' ? 'פעיל' : 'כבוי',
                      `${questions.length} שאלות`,
                      summary,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={() => navigate(listPath)}>
                  חזרה
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  disabled={saving || !dirty}
                  onClick={handleSave}
                >
                  {saving ? 'שומר...' : 'שמירת שאלות'}
                </Button>
              </Box>
            </Box>
        )}
      />

      {flash && (
        <Box sx={{ mb: 1.5 }}>
          <FormAlert severity={flash.severity}>{flash.message}</FormAlert>
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 1.5 }}>
          <FormAlert severity="error">{error}</FormAlert>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {questions.map((q, index) => (
          <Box
            key={`q-${index}`}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: { xs: 1.5, sm: 2 },
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography fontWeight={700}>שאלה {index + 1}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={q.sectionKey}
                    onChange={(e) => updateQuestion(index, { sectionKey: e.target.value })}
                    displayEmpty
                  >
                    {SECTION_OPTIONS.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={() => moveQuestion(index, -1)}
                  aria-label="העלה"
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={index === questions.length - 1}
                  onClick={() => moveQuestion(index, 1)}
                  aria-label="הורד"
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeQuestion(index)}
                  aria-label="מחק שאלה"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <TextField
              label="טקסט השאלה"
              value={q.text}
              onChange={(e) => updateQuestion(index, { text: e.target.value })}
              fullWidth
              multiline
              minRows={2}
              size="small"
              sx={{ mb: 1.5 }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              אפשרויות תשובה (סמנו את הנכונה)
            </Typography>
            <RadioGroup
              value={String(q.correctOption)}
              onChange={(e) => updateQuestion(index, { correctOption: Number(e.target.value) })}
            >
              {[0, 1, 2].map((optIndex) => (
                <Box
                  key={optIndex}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <FormControlLabel
                    value={String(optIndex + 1)}
                    control={<Radio size="small" sx={{ mt: 0.5 }} />}
                    label=""
                    sx={{ m: 0, mr: -0.5 }}
                  />
                  <TextField
                    label={`אפשרות ${optIndex + 1}`}
                    value={q.options[optIndex]}
                    onChange={(e) => updateOption(index, optIndex, e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    minRows={1}
                  />
                </Box>
              ))}
            </RadioGroup>

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => addQuestion(index)}
              sx={{ mt: 0.5 }}
            >
              הוסף שאלה אחרי זו
            </Button>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => addQuestion(null)}
        >
          הוסף שאלה בסוף
        </Button>
        <Button
          size="small"
          variant="contained"
          color="secondary"
          disabled={saving || !dirty}
          onClick={handleSave}
        >
          {saving ? 'שומר...' : 'שמירת שאלות'}
        </Button>
      </Box>
    </Box>
  );
};

export default AmericanExamEditor;
